/**
 * Invoices API
 */
import apiClient from './index'
import type {
  ApiResponse,
  PaginatedResponse,
  PaginationParams,
  Invoice,
  InvoiceCreateData,
  InvoiceUpdateData,
  InvoiceFilters,
  PaymentData,
  GenerateFromTimeData,
} from '@/types'

export const invoicesApi = {
  /**
   * List invoices with filtering and pagination
   */
  async list(params?: PaginationParams & InvoiceFilters): Promise<PaginatedResponse<Invoice>> {
    const response = await apiClient.get<PaginatedResponse<Invoice>>('/invoices', { params })
    return response.data
  },

  /**
   * Get invoice by ID
   */
  async get(id: string): Promise<Invoice> {
    const response = await apiClient.get<ApiResponse<Invoice>>(`/invoices/${id}`)
    return response.data.data
  },

  /**
   * Create a new invoice
   */
  async create(data: InvoiceCreateData): Promise<Invoice> {
    const response = await apiClient.post<ApiResponse<Invoice>>('/invoices', data)
    return response.data.data
  },

  /**
   * Update an invoice
   */
  async update(id: string, data: InvoiceUpdateData): Promise<Invoice> {
    const response = await apiClient.put<ApiResponse<Invoice>>(`/invoices/${id}`, data)
    return response.data.data
  },

  /**
   * Delete an invoice
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/invoices/${id}`)
  },

  /**
   * Send an invoice
   */
  async send(id: string): Promise<Invoice> {
    const response = await apiClient.post<ApiResponse<Invoice>>(`/invoices/${id}/send`)
    return response.data.data
  },

  /**
   * Record a payment
   */
  async recordPayment(id: string, data: PaymentData): Promise<Invoice> {
    const response = await apiClient.post<ApiResponse<Invoice>>(`/invoices/${id}/payment`, data)
    return response.data.data
  },

  /**
   * Void an invoice
   */
  async void(id: string, reason?: string): Promise<Invoice> {
    const response = await apiClient.post<ApiResponse<Invoice>>(`/invoices/${id}/void`, { reason })
    return response.data.data
  },

  /**
   * Get invoice PDF
   */
  async getPdf(id: string): Promise<Blob> {
    const response = await apiClient.get(`/invoices/${id}/pdf`, {
      responseType: 'blob',
    })
    return response.data
  },

  /**
   * Generate invoice from time entries
   */
  async generateFromTime(data: GenerateFromTimeData): Promise<Invoice> {
    const response = await apiClient.post<ApiResponse<Invoice>>('/invoices/generate-from-time', data)
    return response.data.data
  },

  /**
   * List overdue invoices
   */
  async listOverdue(params?: PaginationParams): Promise<PaginatedResponse<Invoice>> {
    const response = await apiClient.get<PaginatedResponse<Invoice>>('/invoices/overdue', { params })
    return response.data
  },
}
